import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
} from '@mui/material';
import { Delete, Edit, Search } from '@mui/icons-material';

import RouteSearch from './RouteSearch';
import RouteView from './RouteView';
import RouteEdit from './RouteEdit';
import ConfirmModal from '../../../common/modals/ConfirmModal';
import Loader from '../../../common/Loader';

import useRouteRequest from '../../../../requests/RouteRequest';

const Route = ({ errorHandler }) => {
  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [route, setRoute] = useState({});
  const [showView, setShowView] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [options, setOptions] = useState([]);
  const [searchData, setSearchData] = useState({});

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState({});
  const { getOptions, searchRoutes, deleteRoute } = useRouteRequest();

  useEffect(() => {
    const fetchOptions = async () => {
      const options = await getOptions(errorHandler);
      setOptions(options);
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [searchData]);

  const handleSearch = async (newSearchData = null) => {
    setLoading(true);
    const routes = await searchRoutes(searchData, errorHandler);
    setRoutes(routes || []);
    setLoading(false);
  };

  const showRoute = (route) => {
    setRoute(route);
    setShowView(true);
  };

  const handleEditRoute = (route) => {
    if (route) setRoute(route);
    setShowEdit(true);
  };

  const handleCloseView = () => {
    setRoute({});
    setShowView(false);
  };

  const handleCloseEdit = () => {
    setRoute({});
    setShowEdit(false);
    handleSearch();
  };

  const confirmDeleteRoute = (route) => {
    setShowConfirmDelete(true);
    setRouteToDelete(route);
  };

  const handleDeleteRoute = async () => {
    await deleteRoute(routeToDelete.route_id, errorHandler);
    setShowConfirmDelete(false);
    handleSearch();
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h5">Routes</Typography>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setShowEdit(true)}
          >
            Add Route
          </Button>
        </Box>
        <RouteSearch onSearch={(s) => setSearchData(s)} options={options} />
      </Grid>
      <Grid size={12}>
        {loading ? (
          <Loader />
        ) : routes.length === 0 ? (
          <Typography>No Routes Retrieved.</Typography>
        ) : (
          <TableContainer sx={{ marginBottom: 4, overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Id</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {routes.map((route, index) => (
                  <TableRow key={`${route.id}_${index}`}>
                    <TableCell>{route.route_id}</TableCell>
                    <TableCell>
                      <Link tabIndex={0} onClick={() => showRoute(route)}>
                        {route.route_name}
                      </Link>
                    </TableCell>
                    <TableCell>{route.route_class_meaning}</TableCell>
                    <TableCell>{route.route_type_meaning}</TableCell>
                    <TableCell>{route.route_status_meaning}</TableCell>
                    <TableCell>{route.priority}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => showRoute(route)} size="small">
                        <Search color="success" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleEditRoute(route)}
                        size="small"
                      >
                        <Edit color="inherit" />
                      </IconButton>
                      {route.route_status === 'N' && (
                        <IconButton
                          onClick={() => confirmDeleteRoute(route)}
                          size="small"
                        >
                          <Delete color="error" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <RouteView
          show={showView}
          route={route}
          onEdit={handleEditRoute}
          onClose={handleCloseView}
          errorHandler={errorHandler}
        />
        <RouteEdit
          show={showEdit}
          route={route}
          options={options}
          onClose={handleCloseEdit}
          errorHandler={errorHandler}
        />
        <ConfirmModal
          show={showConfirmDelete}
          onClose={() => setShowConfirmDelete(false)}
          onConfirm={() => handleDeleteRoute()}
          message={`Are you sure you want to delete ${routeToDelete.route_name}?`}
        />
      </Grid>
    </Grid>
  );
};

export default Route;
